import React, { useState, useRef, useCallback,useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiFileText, FiAlertCircle, FiTrash2, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import CommissionScanClientFiles, { AmsFileSelection } from '../commissionscan/CommissionScanClientFiles';
import '../commissionscan/CommissionScan.css';

const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

type TabType = 'local' | 'client';

interface Props { onGoToHistory?: () => void; }

const LossRunScanUpload: React.FC<Props> = ({ onGoToHistory }) => {
  const [activeTab,       setActiveTab]       = useState<TabType>('local');
  const [file,            setFile]            = useState<File | null>(null);
  const [selectedAmsFile, setSelectedAmsFile] = useState<AmsFileSelection | null>(null);
  const [isDragging,      setIsDragging]      = useState(false);
  const [uploadLoading,   setUploadLoading]   = useState(false);
  const [uploadError,     setUploadError]     = useState('');
  const [successMessage,  setSuccessMessage]  = useState(false);
  const [transactionId,   setTransactionId]   = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Feedback */
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess,    setFeedbackSuccess]    = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const closeFeedback = useCallback(() => {
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
  }, []);

  useEffect(() => {
    if (!feedbackAnchor) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        closeFeedback();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackAnchor, closeFeedback]);

  const openFeedbackPopup = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const onThumbsUpClick = useCallback(() => {
    const next = feedbackRating === true ? null : true;
    closeFeedback();
    axios.post(`${(process.env.REACT_APP_DataFabric_Url  || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 7, TransactionId: transactionId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).catch(() => {});
  }, [feedbackRating, transactionId, feedbackText, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    const next = feedbackRating === false ? null : false;
    closeFeedback();
    axios.post(`${(process.env.REACT_APP_DataFabric_Url  || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 7, TransactionId: transactionId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).catch(() => {});
  }, [feedbackRating, transactionId, feedbackText, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please keep feedback under 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url  || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 7, TransactionId: transactionId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); setFeedbackSuccess(true); setTimeout(closeFeedback, 1500); })
      .catch(() => { setFeedbackSubmitting(false); });
  }, [transactionId, feedbackText, feedbackRating, closeFeedback]);

  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
    );
    if (first) {
      setFile(first);
      setUploadError('');
    } else {
      setUploadError('Please select a PDF file.');
      setTimeout(() => setUploadError(''), 3000);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) pickFile(e.target.files);
    e.target.value = '';
  };

  const canScan = !uploadLoading && (activeTab === 'local' ? !!file : !!selectedAmsFile);

  const onStartScan = useCallback(async () => {
    const isAms = activeTab === 'client';
    if (!isAms && !file) return;
    if (isAms && !selectedAmsFile) return;

    setUploadLoading(true);
    setUploadError('');

    try {
      const insuredId = isAms ? (selectedAmsFile?.insuredId || '') : '';
      const folderId  = isAms ? (selectedAmsFile?.folderId  || '') : '';
      const fileIds   = isAms ? [selectedAmsFile?.file.id   || ''] : [];

      const formData = new FormData();
      if (!isAms && file) formData.append('file', file);
      if (isAms) fileIds.forEach(id => formData.append('amsFileIds', id));
      formData.append('folderId',           folderId);
      formData.append('insuredId',          insuredId);
      formData.append('isFileUploadFromAMS', String(isAms));
      formData.append('NcToken',            getNcToken());
      formData.append('selectionOptionJson', JSON.stringify({
        insuredId,
        folderId,
        fileIds,
        isFileUploadFromAMSParam: isAms,
      }));

      const res = await axios.post(`${(process.env.REACT_APP_DataFabric_Url  || '')}/api/LossRunScan/scans`, formData, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      setTransactionId(res.data?.responseJson?.transactionId || '');
      setSuccessMessage(true);
      setFile(null);
      setSelectedAmsFile(null);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setUploadError(err.response.data || 'Insufficient balance.');
      } else {
        setUploadError(err.response?.data?.errorMessage || 'Error while submitting the scan.');
        setTimeout(() => setUploadError(''), 4000);
      }
    } finally {
      setUploadLoading(false);
    }
  }, [file, selectedAmsFile, activeTab]);

  const handleCancel = useCallback(() => {
    setActiveTab('local');
    setFile(null);
    setSelectedAmsFile(null);
    setIsDragging(false);
    setUploadLoading(false);
    setUploadError('');
    setSuccessMessage(false);
    setTransactionId('');
    closeFeedback();
  }, [closeFeedback]);

  return (
    <>
    <div className="cs-main" style={{ padding: 0, gap: 14 }}>

      {/* Upload Tabs */}
      <div className="cs-tabs">
        <button
          className={`cs-tab${activeTab === 'local' ? ' cs-tab--active' : ''}`}
          onClick={() => { setActiveTab('local'); setSuccessMessage(false); setSelectedAmsFile(null); }}
        >
          Select Document from Local Drive
        </button>
        <button
          className={`cs-tab${activeTab === 'client' ? ' cs-tab--active' : ''}`}
          onClick={() => { setActiveTab('client'); setSuccessMessage(false); setFile(null); }}
        >
          Select Document from Client Profile
        </button>
      </div>

      {/* Tab content */}
      <div className="cs-content">
        {successMessage ? (
          <div className="cs-success-banner-wrap">
            <div className="cs-success-banner">
              <div className="cs-success-banner__icon">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="cs-success-banner__body">
                <p className="cs-success-banner__title">File uploaded successfully!</p>
                <p className="cs-success-banner__desc">
                  We are processing your file. Please check the{' '}
                  <button className="cs-success-link" onClick={onGoToHistory}>History</button>
                  {' '}tab to find out the file's current progress status and feel free to provide your{' '}
                  <button className="cs-success-link" onClick={openFeedbackPopup}>
                    Feedback here
                  </button>
                </p>
              </div>
            </div>
          </div>
        ) : activeTab === 'local' ? (
          <div
            className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
          >
            {!file ? (
              <>
                <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                <p className="cs-dropzone__title">Drag & Drop your Loss Run PDF here</p>
                <p className="cs-dropzone__or">or</p>
                <motion.button
                  className="cs-browse-btn"
                  onClick={() => fileInputRef.current?.click()}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Browse Files
                </motion.button>
                <p className="cs-dropzone__types">Allowed: PDF only</p>
              </>
            ) : (
              <div className="cs-file-card">
                <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                <div className="cs-file-card__info">
                  <span className="cs-file-card__name">{file.name}</span>
                  <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                </div>
                <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove">
                  <FiTrash2 size={15} />
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </div>
        ) : (
          <CommissionScanClientFiles onFileSelected={setSelectedAmsFile} />
        )}
      </div>

      {/* Info banner */}
      {!successMessage && (
        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>
            This tool works best with readable PDFs. Scanned or image-based PDFs may not process correctly.
            Consider converting to a readable format using OCR before uploading.
          </span>
        </div>
      )}

      {uploadError && (
        <div style={{ color: '#dc2626', fontSize: 12, padding: '4px 0' }}>{uploadError}</div>
      )}

      {/* Footer */}
      <div className="cs-footer">
        <span className="cs-footer__status">
          {!successMessage && (activeTab === 'local'
            ? (!file ? 'No file selected.' : `${file.name} ready to scan`)
            : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)
          )}
        </span>
        <div className="cs-footer__actions">
          {successMessage ? (
            <motion.button
              className="cs-btn-start"
              onClick={handleCancel}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              ✦ New Scan
            </motion.button>
          ) : (
            <>
              <button className="cs-btn-cancel" onClick={handleCancel} disabled={uploadLoading}>Cancel</button>
              <motion.button
                className="cs-btn-start"
                disabled={!canScan}
                whileHover={{ scale: canScan ? 1.03 : 1 }}
                whileTap={{ scale: canScan ? 0.97 : 1 }}
                onClick={onStartScan}
              >
                {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
              </motion.button>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Feedback popup */}
    {feedbackAnchor && (
      <div
        ref={feedbackRef}
        className="csfb-popup"
        style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
      >
        <h4 className="csfb-title">Feedback</h4>
        <textarea
          className="csfb-textarea"
          placeholder="Enter your feedback here"
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          rows={4}
        />
        <div className="csfb-rating">
          <button
            className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
            onClick={onThumbsUpClick}
            title="Thumbs up"
          >
            <FiThumbsUp size={20} />
          </button>
          <button
            className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
            onClick={onThumbsDownClick}
            title="Thumbs down"
          >
            <FiThumbsDown size={20} />
          </button>
        </div>
        {feedbackSuccess && <p className="csfb-success">Feedback submitted successfully!</p>}
        {feedBackError   && <p className="csfb-error">{feedBackError}</p>}
        <div className="csfb-actions">
          <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
          <button
            className="csfb-btn-submit"
            disabled={feedbackSubmitting}
            onClick={onSubmitFeedbackClick}
          >
            {feedbackSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default LossRunScanUpload;

